"use client";

import { FeedEvents, trackEvent } from "@/analytics";
import { useEffect, useState } from "react";

import { Check } from "lucide-react";
import { Comedian } from "@/data/comedians";
import { FOLLOW_LOCATIONS } from "@/data/follows";
import FeedCard from "./FeedCard";
import { Follow } from "@/components/checkout";
import HideButton from "@/components/common/HideButton";
import Image from "next/image";
import Link from "next/link";
import { getCDNImageURL } from "@/data/images";
import { FeedPost } from "../types";

interface Props {
  post: FeedPost;
  comedian: Comedian;
  isLoggedOutState: boolean;
  userFollowsComedian: boolean;
  hideEnabled?: boolean;
  onHide?: (postId: string) => void;
  pageTypeLabel?: string;
}

export default function FeaturedComedianFeedCard({
  post,
  comedian,
  isLoggedOutState,
  userFollowsComedian,
  hideEnabled = false,
  onHide,
  pageTypeLabel = "Comedian",
}: Props) {
  const [profileImg, setProfileImg] = useState<string | null>(null);
  const comedianPageUrl = `/${comedian.slug}`;
  const redirectTo = isLoggedOutState ? `/${comedian.slug}` : "";

  useEffect(() => {
    if (!comedian.about_img) return;
    setProfileImg(getCDNImageURL(comedian.about_img));
  }, [comedian.about_img]);

  if (!profileImg) {
    // TODO: add shimmer, or image logo shimmer
    return null;
  }

  return (
    <FeedCard>
      <div className="relative flex flex-col gap-4 md:flex-row md:gap-6">
        {/* mobile-only title */}
        <CardTitle
          className="flex flex-col md:hidden"
          comedianName={comedian.display_name}
          comedianPageUrl={comedianPageUrl}
          pageTypeLabel={pageTypeLabel}
        />
        {/* hero image */}
        <div className="flex flex-row justify-center">
          <Image
            src={profileImg}
            width={260}
            height={260}
            alt={`Profile picture of ${comedian.display_name}`}
            className="object-cover object-top w-full h-auto bg-gray-300 rounded-md aspect-square"
          />
        </div>
        {/* body */}
        <div className="flex-1">
          <div className="flex flex-col justify-start h-full gap-4 md:py-3 md:gap-6">
            {/* text content */}
            <div className="flex flex-col gap-3">
              {/* desktop-only title */}
              <CardTitle
                className="flex-col hidden md:flex"
                comedianName={comedian.display_name}
                comedianPageUrl={comedianPageUrl}
                pageTypeLabel={pageTypeLabel}
              />
              <p className="text-[#595959] line-clamp-5 md:line-clamp-4 text-xs leading-normal">
                {comedian.about}
              </p>
            </div>
            {/* actions */}
            <div className="flex flex-col gap-3 md:flex-row">
              {userFollowsComedian ? (
                <button
                  type="button"
                  disabled
                  aria-disabled="true"
                  className="flex flex-row items-center gap-0.5 md:gap-1 text-[14px] md:text-[16px] w-[unset] text-primary text-xs md:text-sm font-medium bg-transparent px-1 py-2.5 md:py-3 self-start"
                >
                  <Check width={12} height={12} strokeWidth={3} />
                  <span>Subscribed</span>
                </button>
              ) : (
                <Follow
                  isLoggedOutState={isLoggedOutState}
                  redirectTo={redirectTo}
                  buttonText="Subscribe"
                  followedLocation={
                    FOLLOW_LOCATIONS.FEED_FEATURED_COMEDIAN_CARD
                  }
                  comedianToFollow={comedian}
                  customCSSButtonClass="btn font-medium px-2 w-full md:w-44 py-[10px]"
                  customCSSContainerClass="block"
                />
              )}
              <Link
                href={`${comedianPageUrl}/tickets`}
                onClick={() =>
                  trackEvent(
                    FeedEvents.CLICKED_TICKETS_AND_MORE_FEATURED_COMEDIAN
                  )
                }
                type="button"
                className="btn text-center border border-primary text-primary font-medium bg-transparent px-2 py-[10px] w-full md:w-44"
              >
                Tickets & More
              </Link>
            </div>
          </div>
        </div>
        {hideEnabled && (
          <HideButton
            comedianId={comedian.id}
            postId={post.id}
            onHide={onHide}
            location="feed-featured-comedian-card"
            label="Hide"
            iconColor="stroke-[#3F3F3F]"
            className="absolute top-0 right-0 h-[36px] border-none shadow-none bg-black/6 hover:bg-black/8 focus:bg-black/10"
            iconHeight={18}
            iconWidth={18}
          />
        )}
      </div>
    </FeedCard>
  );
}

function CardTitle({
  className,
  comedianName,
  comedianPageUrl,
  pageTypeLabel = "Comedian",
}: {
  className?: string;
  comedianName: string | null;
  comedianPageUrl?: string;
  pageTypeLabel?: string;
}) {
  return (
    <div className={className}>
      <p className="uppercase text-[14px] font-medium text-[#535559] pb-2 leading-none">
        Featured {pageTypeLabel}
      </p>
      {comedianPageUrl ? (
        <Link href={comedianPageUrl} className="w-fit">
          <h3 className="font-sans text-2xl font-extrabold leading-none normal-case">
            {comedianName}
          </h3>
        </Link>
      ) : comedianName ? (
        <h3 className="font-sans text-2xl font-extrabold leading-none normal-case w-fit">
          {comedianName}
        </h3>
      ) : null}
    </div>
  );
}
